import React, { useEffect, useState } from 'react';
import { ccmApi } from '../lib/api';
import {
  optionLabel, optionName, modeLabel, modelOptionLabel, prefsBody, charCount, fieldMessage, copyFor,
} from '../lib/note-expander-v2';

// Note Expander v2 Preferences (PR 5-UI-1, O191; DESIGN §5.3, mockup "prefs"). Everything offered
// comes from the server: the style choices from GET /v2/me/prefs (`choices`, read from the pack),
// the models from GET /v2/models. A save is the whole settings object (PUT /v2/me/prefs validates
// every field and names the one it refuses). "Your old style notes" are the earlier expander's,
// shown read-only to their author with a copy button (OQ-8); they are never applied.
// The Style row (PR 8a-UI, mockup "prefs"): "Auto customize" is on while the approved profile carries
// a learned style (`style.approved.hasLearned`, the test the runs use). "Set by hand" stops using it
// (POST /v2/me/style/standard); "Auto customize" brings the last approved one back (/restore), or
// opens Your style to build one.

const MAXIMAL_EXTENSION_LINE = 'Maximal highlights on swept notes need the Chrome extension. Without it, a swept Maximal note carries unmarked inferred text until it is reviewed here.';

export function NoteExpanderV2Prefs({ view, models, copy, onSaved, style, onStyleChange, onOpenStyle }) {
  const [form, setForm] = useState(view.settings);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [errorField, setErrorField] = useState(null);
  const [error, setError] = useState('');
  const [styleBusy, setStyleBusy] = useState(false);
  const [styleError, setStyleError] = useState('');

  useEffect(() => { setForm(view.settings); }, [view]);

  const choices = view.choices || { options: {}, modes: [], notesMaxChars: 1000 };
  const entries = (models && models.models) || [];
  const notes = form.notes || '';
  const notesCount = charCount(notes);
  const notesOver = notesCount > choices.notesMaxChars;

  function set(patch) { setForm((prev) => ({ ...prev, ...patch })); setSaved(false); }
  function setOption(key, value) { set({ options: { ...form.options, [key]: value } }); }

  async function save() {
    setSaving(true); setError(''); setErrorField(null); setSaved(false);
    try {
      const next = await ccmApi.ne2SavePrefs(prefsBody(form));
      setSaved(true);
      onSaved(next);
    } catch (e) {
      const body = (e && e.body) || {};
      if (e && e.status === 400 && body.field) { setErrorField(body.field); setError(fieldMessage(body.field, choices.notesMaxChars)); }
      else if (e && e.status === 403 && body.reason) setError(copyFor(copy, 'request', body.reason) || 'You cannot change these preferences.');
      else setError('Your preferences could not be saved. Try again.');
    } finally {
      setSaving(false);
    }
  }

  function copyOldNotes() {
    const old = view.oldNotes && view.oldNotes.text;
    if (!old) return;
    const joined = notes.trim() ? `${notes.trim()}\n${old}` : old;
    set({ notes: [...joined].slice(0, choices.notesMaxChars).join('') });
  }

  const auto = !!(style && style.approved && style.approved.hasLearned);
  async function setAuto(on) {
    if (on === auto || styleBusy) return;
    setStyleBusy(true); setStyleError('');
    try {
      if (on) await ccmApi.ne2StyleRestore();
      else await ccmApi.ne2StyleStandard();
    } catch (e) {
      const reason = e && e.body && e.body.reason;
      if (on && reason === 'no_learned_style') onOpenStyle();
      else setStyleError(copyFor(copy, 'style', reason) || 'Your style could not be changed. Try again.');
      setStyleBusy(false);
      return;
    }
    try {
      await onStyleChange();
    } catch (_) {
      setStyleError('Done. The page could not refresh: reload it to see the change.');
    } finally {
      setStyleBusy(false);
    }
  }

  const fieldError = (name) => (errorField === name ? <div className="ne2-field-error" role="alert">{error}</div> : null);

  return (
    <div className="ne2-prefs">
      <p className="ne2-prefs-lede">
        These choose how your notes are written. The style options are fixed wording; your style notes are
        sent as data and can only change style, never add content.
      </p>

      {style && (
        <section className="ne2-pref">
          <div className="ne2-pref-head">
            <h3>Style</h3>
            <p>Auto customize learns from your own signed notes. You approve it first.</p>
          </div>
          <div className="ne2-seg" role="radiogroup" aria-label="Style">
            <button type="button" role="radio" aria-checked={auto} disabled={styleBusy}
                    className={'ne2-seg-btn' + (auto ? ' is-on' : '')} onClick={() => setAuto(true)}>Auto customize</button>
            <button type="button" role="radio" aria-checked={!auto} disabled={styleBusy}
                    className={'ne2-seg-btn' + (!auto ? ' is-on' : '')} onClick={() => setAuto(false)}>Set by hand</button>
          </div>
          <p className="ne2-pref-note">
            {auto ? 'While it\'s on, your expansions also follow your examples and your own wording for normal-exam phrases.'
              : 'Your expansions follow only the settings below.'}{' '}
            <button type="button" className="btn sm" onClick={onOpenStyle}>Review your style</button>
          </p>
          {styleError && <div className="ne2-field-error" role="alert">{styleError}</div>}
        </section>
      )}

      <section className="ne2-pref">
        <div className="ne2-pref-head">
          <h3>Default mode</h3>
          <p>You can switch it on each note.</p>
        </div>
        <div className="ne2-seg" role="radiogroup" aria-label="Default mode">
          {choices.modes.map((m) => (
            <button key={m} type="button" role="radio" aria-checked={form.defaultMode === m}
                    className={'ne2-seg-btn' + (form.defaultMode === m ? ' is-on' : '')}
                    onClick={() => set({ defaultMode: m })}>{modeLabel(m)}</button>
          ))}
        </div>
        {form.defaultMode === 'maximal' && <p className="ne2-pref-note">{MAXIMAL_EXTENSION_LINE}</p>}
        {fieldError('defaultMode')}
      </section>

      <section className="ne2-pref">
        <div className="ne2-pref-head">
          <h3>Default model</h3>
          <p>Any model in the note expander's list. You can switch it on each expansion.</p>
        </div>
        <select className="ne2-select" value={form.defaultModel || ''} aria-label="Default model"
                onChange={(e) => set({ defaultModel: e.target.value || null })}>
          <option value="">Practice default</option>
          {entries.map((m) => (
            <option key={m.alias} value={m.alias}>{modelOptionLabel(m, view.practiceDefaultModel)}</option>
          ))}
        </select>
        {models && models.marksNote && <p className="ne2-pref-note">{models.marksNote}</p>}
        {fieldError('defaultModel')}
      </section>

      {Object.keys(choices.options).map((key) => (
        <section className="ne2-pref" key={key}>
          <div className="ne2-pref-head"><h3>{optionName(key)}</h3></div>
          <div className="ne2-seg" role="radiogroup" aria-label={optionName(key)}>
            {choices.options[key].map((value) => (
              <button key={value} type="button" role="radio" aria-checked={form.options[key] === value}
                      className={'ne2-seg-btn' + (form.options[key] === value ? ' is-on' : '')}
                      onClick={() => setOption(key, value)}>{optionLabel(key, value)}</button>
            ))}
          </div>
          {fieldError('options.' + key)}
        </section>
      ))}

      <section className="ne2-pref">
        <div className="ne2-pref-head">
          <h3>Style notes</h3>
          <p>Optional, up to {choices.notesMaxChars.toLocaleString('en-US')} characters. Style only; it can't add content.</p>
        </div>
        <textarea className="ne2-notes" rows={4} value={notes} aria-label="Style notes"
                  placeholder={'For example: prefer "patient" over "pt"; keep plans short.'}
                  onChange={(e) => set({ notes: e.target.value })} />
        <div className={'ne2-count' + (notesOver ? ' is-over' : '')} aria-live="polite">
          {notesCount.toLocaleString('en-US')} / {choices.notesMaxChars.toLocaleString('en-US')}
        </div>
        {fieldError('notes')}
      </section>

      {view.oldNotes && (
        <section className="ne2-pref ne2-old">
          <div className="ne2-pref-head">
            <h3>Your old style notes</h3>
            <p>From the earlier expander. Not applied; copy any you want.</p>
          </div>
          <blockquote className="ne2-old-text">{view.oldNotes.text}</blockquote>
          <button type="button" className="btn sm" onClick={copyOldNotes}>Copy to style notes</button>
        </section>
      )}

      <div className="ne2-save">
        <button type="button" className="btn accent" onClick={save} disabled={saving || notesOver}>
          {saving ? 'Saving…' : 'Save preferences'}
        </button>
        {saved && <span className="ne2-saved" role="status">Saved. New expansions use these.</span>}
        {error && !errorField && <span className="ne2-field-error" role="alert">{error}</span>}
        {view.profile && <span className="ne2-version">Version {view.profile.version}</span>}
      </div>
    </div>
  );
}
